import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { createEmailMessage, fetchEmails } from "./components/emailService"; // Update the service paths as necessary
import { fetchCustomers } from "./components/smsService";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh"; // Import the refresh icon



const EmailsPage = () => {
  const theme = useTheme();
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const [pastEmails, setPastEmails] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({
    subject: "",
    body: "",
  });
  const navigate = useNavigate();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    const loadEmailCampaignsAndCustomers = async () => {
      try {
        const emailCampaigns = await fetchEmails();
        const customerList = await fetchCustomers();

        setPastEmails(emailCampaigns);
        setCustomers(customerList);
      } catch (error) {
        console.error("Error loading email campaigns or customers:", error);
      }
    };

    loadEmailCampaignsAndCustomers();
  }, []);

  const handleSelectAllCustomers = () => {
    if (isAllSelected) {
      setSelectedCustomers([]);
      setSelectedEmails([]);
    } else {
      const allSelected = customers;
      setSelectedCustomers(allSelected);
      setSelectedEmails(allSelected.map((customer) => customer.email));
    }
  };
//single select
  const handleSelectSingleCustomer = (customer) => {
    const isSelected = selectedEmails.includes(customer.email);
    if (isSelected) {
      setSelectedCustomers((prev) =>
        prev.filter((c) => c.email !== customer.email)
      );
      setSelectedEmails((prev) =>
        prev.filter((email) => email !== customer.email)
      );
    } else {
      setSelectedCustomers((prev) => [...prev, customer]);
      setSelectedEmails((prev) => [...prev, customer.email]);
    }
  };
 

  const handleSendEmail = async () => {
    if (emailContent && selectedCustomers.length) {
      try {
        for (const customer of selectedCustomers) {
          const emailInput = {
            body: emailContent,
            subject: subject,
            recipientEmail: customer.email,
            createdBy: parseInt("currentUserId", 10), // Replace with the actual user ID
          };

          await createEmailMessage(emailInput);
        }
        setShowSuccessMessage(true);
        setSuccessMessageText("Email sent successfully!");
        setEmailContent("");
        setSubject("");
        setSelectedCustomers([]);
        setSelectedEmails([]);
        const emails = await fetchEmails();
        setPastEmails(emails);
      } catch (error) {
        console.error("Error sending email:", error);
        setShowErrorMessage(true);
        setErrorMessageText("Failed to send email. Please try again.");
      }
    } else {
      alert("Please fill in all fields");
    }
  };

  const handleCustomerChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      handleSelectAllCustomers();
    } else {
      const selectedCustomer = customers.find(
        (customer) => customer.customerName === value[value.length - 1]
      );
      if (selectedCustomer) {
        handleSelectSingleCustomer(selectedCustomer); // Handle single customer selection
      }
    }
  };

  const isAllSelected = selectedCustomers.length === (customers?.length || 0);

  const handleCloseSnackbar = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };


  const handleEmailSelect = (id) => {
    const selectedEmail = pastEmails.find((email) => email.messageId === id);
    if (selectedEmail) {
      setPreviewContent({
        subject: selectedEmail.subject,
        body: selectedEmail.body,
      });
      setOpenPreview(true);
      setEmailContent(selectedEmail.body);
      setSubject(selectedEmail.subject);
    }
  };
  const handleResetFields = () => {
    setSubject("");
    setEmailContent("");
  };

  const columns = [
    { field: "messageId", headerName: "ID", width: 50 },
    { field: "subject", headerName: "Subject", flex: 2 },
    { field: "body", headerName: "Email Content", flex: 2 },
    { field: "status", headerName: "Status", flex: 1 },

    {
      field: "createdDate",
      headerName: "Created Date",
      width: 90,
      valueGetter: (params) =>
        new Date(params.row.createdDate).toLocaleDateString(),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <Button onClick={() => handleEmailSelect(params.row.messageId)}>
          PREVIEW
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2>Create a New Email </h2>
          <IconButton onClick={handleResetFields} color="primary" style={{ marginLeft: "10px" }}>
            <RefreshIcon />
          </IconButton>
        </div>
      </div>

      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <TextField
          label="Subject"
          name="subject"
          variant="outlined"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <TextField
          label="Email Content"
          name="body"
          variant="outlined"
          multiline
          rows={4}
          value={emailContent}
          onChange={(e) => setEmailContent(e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <InputLabel>Send to Customers</InputLabel>
        <Select
          multiple
          value={selectedCustomers.map((customer) => customer.customerName)} // Display customer names
          onChange={handleCustomerChange}
          renderValue={(selected) =>
            selected.length ? selected.join(", ") : "Select Customers"
          }
        >
          <MenuItem value="all">
            <Checkbox
              checked={isAllSelected}
              onChange={handleSelectAllCustomers}
            />
            <ListItemText primary="Select All" />
          </MenuItem>

          {customers.length > 0 &&
            customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.customerName}>
                <Checkbox
                  checked={selectedEmails.includes(customer.email)}
                  onChange={() => handleSelectSingleCustomer(customer)}
                />
                <ListItemText
                  primary={`${customer.customerName} (${customer.email})`}
                />
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSendEmail}
        disabled={!emailContent || !selectedCustomers.length}
      >
        Send Email
      </Button>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={pastEmails}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(row) => row.messageId}
          disableSelectionOnClick
        />
      </Box>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessageText}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessageText}
        </Alert>
      </Snackbar>
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
      <DialogTitle>{previewContent.subject}</DialogTitle>
      <DialogContent>
        <p>{previewContent.body}</p>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenPreview(false)} variant= "contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>;
    </div>
  );
};

export default EmailsPage;
